import React, {useState, useEffect, useRef, useMemo} from 'react';
import {ObjectId, UUID} from 'bson';
import {ScrollView, View, Text, Pressable, Modal} from 'react-native';
import {useIsFocused} from '@react-navigation/native';
import moment from 'moment';
import {SubmissionStatuses} from '../../Constants/SubmissionStatuses';
import {LOCK_STATUS} from '../../Constants/AppConstants';
import {TemplateCategoryTypes} from '../../Constants/TemplateCategoryTypes';
import {Formats} from '../../Constants/Formats';
import Icon, {IconValue} from '../Shared/Icon';
import {Icons} from '../../Constants/Icons';
import CustomPressable from '../Shared/CustomPressable';
import SearchBox, {Filter, FilterValue} from '../Shared/SearchBox';
import DynamicPDF from '../DynamicForm/DynamicPDF';
import {ReportsStyleSheet} from '../../Styles/ReportsStyles';
import {CommonStyleSheet} from '../../Styles/Shared/CommonStyles';
import {ControlsStyleSheet} from '../../Styles/Shared/Controls';
import {Navigation} from '../../Constants/Navigation';
import Colors from '../../Styles/Shared/Colors';
import {useTemplateConversion} from '../../Providers/TemplateConversionProvider';
import ConfirmModal from './ConfirmModal';
import {usePowerSyncData} from '../../Providers/PowerSyncDataProvider';
import {
  Template,
  TemplateVersion,
  Person,
  SubmissionRelation,
  SQLUser,
  SubmissionStatus,
  Submission,
  SubmissionLink,
} from '../../Models/PowerSyncModels/models';
import {TemplateTypes} from '../../Constants/TemplateCategoryTypes';
import {
  CATEGORY,
  METADATA_KEYS,
  PERMISSIONS,
} from '../../Constants/AppConstants';
import {MongoIdSqlId} from '../../Types/DtoTypes';
import {usePermission} from '../../Providers/PermissionProvider';
import {FilterTypes} from '../../Constants/General';
import {ListDataTypes} from '../../Constants/ListDataTypes';
import Paginator from './Paginator';
import {usePaginatedRecords} from '../../Hooks/usePaginatedRecords';
import {usePowerSyncUser} from '../../Providers/PowerSyncUserProvider';
import usePowerSyncWriteData from '../../Hooks/usePowerSyncWriteData';
import {ProjectSitesStyleSheet} from '../../Styles/ProjectSiteStyles';
import {SortConfig} from '../../Components/Views/Reports';
import { IGeneralHelperService } from '../../Services/Interfaces/IGeneralHelperService';
import GeneralHelperService from '../../Services/GeneralHelperService';

export declare type RecordListProps = {
  title?: string;
  records?: Submission[];
  formattedRecords?: Record[];
  showSearch?: boolean;
  showDrafts?: boolean;
  showGroup?: boolean;
  showAssignedTo?: boolean;
  showInvestigation?: boolean;
  hasMetaData?: boolean;
  metaDataKey_title?: string;
  metaDataKey_project?: string;
  showFilters?: boolean;
  filters?: Filter[];
  showSorters?: boolean;
  onRecordPressed?: (recordId: string) => void;
  setUnselectCommand?: (func: () => void) => void;
  navigation: any;
  disabled?: boolean;
  useLocalIcons?: boolean;
  category?: string;
  filterValue: FilterValue;
  onChangeFilter?: (filterValue: FilterValue) => void;
  sortConfig?: SortConfig;
  setSortConfig?: React.Dispatch<React.SetStateAction<SortConfig>>;
};

export declare type Record = {
  key: number;
  id: string;
  templateId?: string;
  personId?: string;
  firstAidAttendant?: string;
  projectId?: string;
  equipmentType?: string;
  equipment?: string;
  name: string;
  status: string;
  isLocked: boolean;
  datetime: Date;
  icon: string;
  assignedText: string;
  createdBy: string;
  investigationRequired?: boolean;
  hasInvestigation?: boolean;
  investigationClosed?: boolean;
  displayId?: string | null;
};

const RecordList = (props: RecordListProps): React.ReactElement => {
  const {
    templates,
    templateVersions,
    submissionLinks,
    SQLUsers,
    submissions,
    equipmentHeavyProfiles,
    equipmentOtherProfiles,
    equipmentVehicleProfiles,
    submissionRelations,
    people,
    submissionStatuses: db_submissionStatuses,
    powersyncReady,
  } = usePowerSyncData();
  const user = usePowerSyncUser();

  const {
    deleteItems,
    updateSubmission,
    createSubmissionLink,
    createSubmission,
    deleteSubmissionAttachments,
  } = usePowerSyncWriteData();

  const [records, setRecords] = useState<Record[]>(new Array<Record>());
  const [selectedRecord, setSelectedRecord] = useState<Record>();
  const [search, setSearch] = useState('');
  const {convertSubmission, canConvertSubmission} = useTemplateConversion();
  const [canConvertSelected, setCanConvertSelected] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [isShowingPDFModal, setIsShowingPDFModal] = useState(false);
  const [recordToPDF, setRecordToPDF] = useState<Record>();

  const [canElevate, setCanElevate] = useState<boolean>();
  const [canDelete, setCanDelete] = useState<boolean>();
  const [peopleNeeded, setPeopleNeeded] = useState<Person[]>([]);
  const [usersNeeded, setUsersNeeded] = useState<SQLUser[]>([]);
  const [relationsNeeded, setRelationsNeeded] = useState<SubmissionRelation[]>(
    [],
  );
  const [hasDeletePermission, setHasDeletePermission] =
    useState<boolean>(false);
  const [hasInvestigationPermission, setHasInvestigationPermission] =
    useState<boolean>(false);
  const [sqlUserId, setSqlUserId] = useState('');
  const [templatesWithInvestigation, setTemplatesWithInvestigation] = useState<
    string[]
  >([]);
  const helperService: IGeneralHelperService = new GeneralHelperService();
  
  const sortedRecords = useMemo(() => {
    if (!records || records.length === 0) {
      return [];
    }
    const sortableItems = [...records];
    if (!props.sortConfig) {
      return sortableItems;
    }
    const {key, direction} = props.sortConfig;
    const dir = direction === 'ascending' ? 1 : -1;

    sortableItems.sort((a, b) => {
      try {
        switch (key) {
          case 'date': {
            const timeA = a.datetime?.getTime() || 0;
            const timeB = b.datetime?.getTime() || 0;
            return (timeA - timeB) * dir;
          }
          case 'az': {
            const getSuffix = (name: string) => {
              if (!name) return '';
              const index = name.indexOf('-');

              if (index === -1) {
                return name;
              }

              return name.substring(index + 1).trim();
            };

            const suffixA = getSuffix(a.name);
            const suffixB = getSuffix(b.name);

            return suffixA.localeCompare(suffixB) * dir;
          }
          case 'incidentId': {
            const idA = parseInt(a.displayId?.split('_')[1] || '0', 10) || 0;
            const idB = parseInt(b.displayId?.split('_')[1] || '0', 10) || 0;
            return (idA - idB) * dir;
          }
          default:
            return 0;
        }
      } catch (error) {
        console.error('Error during sorting:', error);
        return 0;
      }
    });
    return [...sortableItems];
  }, [records, props.sortConfig]);

  const submittedPaginatedProps = useMemo(() => {
    return {
      data: sortedRecords,
      filters: props.filterValue,
      search,
      pageSize: 10,
      statusFilter: [
        SubmissionStatuses.SUBMITTED,
        SubmissionStatuses.COMPLETE,
        SubmissionStatuses.DISMISSED,
      ],
      sortConfig: props.sortConfig,
    };
  }, [sortedRecords, props.filterValue, search, props.sortConfig]);
  const {
    paginatedData: submittedRecords,
    paginator: submittedPaginator,
    totalRecords: submittedTotal,
  } = usePaginatedRecords(submittedPaginatedProps);

  const draftPaginatedProps = useMemo(
    () => ({
      data: sortedRecords,
      filters: props.filterValue,
      search,
      pageSize: 10,
      statusFilter: [SubmissionStatuses.DRAFT],
      sortConfig: props.sortConfig,
    }),
    [sortedRecords, props.filterValue, search, props.sortConfig],
  );

  const {
    paginatedData: draftRecords,
    paginator: draftPaginator,
    totalRecords: draftTotal,
  } = usePaginatedRecords(draftPaginatedProps);

  const {hasPermission} = usePermission();

  const isFocused = useIsFocused();
  const deleteModalCommand = useRef({
    openModal: () => {},
    setOpenModal(func: () => void) {
      this.openModal = func;
    },
  });

  //Obj for accessing open modal func in child
  const elevateModalCommand = useRef({
    openModal: () => {},
    setOpenModal(func: () => void) {
      this.openModal = func;
    },
  });

  const noDeleteModalCommand = useRef({
    openModal: () => {},
    setOpenModal(func: () => void) {
      this.openModal = func;
    },
  });

  const allVersionsNeeded = useMemo<
    {id: string; name: string; icon: string}[]
  >(() => {
    let result: {id: string; name: string; icon: string}[] = [];

    let versionsMapped = templateVersions.map(v => ({
      id: v._id,
      templateId: v.templateId,
      name: v.displayName ?? v.name,
    }));

    versionsMapped.forEach(v => {
      let template = templates.find(x => x._id === v.templateId);

      if (template && template.iconSvg) {
        let templateIcon = helperService.getTemplateIcon(template.iconSvg, Colors.darkestGreen, Colors.white);
        
        result.push({id: v.id, name: v.name, icon: templateIcon});
      }
    });

    return result;
  }, [templateVersions, templates]);

  const templatesNeeded = useMemo<
    {versionId: string; templateId: string; reportName: string}[]
  >(() => {
    let result: {versionId: string; templateId: string; reportName: string}[] =
      [];

    let versionsMapped = templateVersions.map(v => ({
      id: v._id,
      templateId: v.templateId,
    }));

    versionsMapped.forEach(v => {
      let template = templates.find(x => x._id === v.templateId);

      if (template && template.iconSvg)
        result.push({
          versionId: v.id,
          templateId: template._id,
          reportName: template.name,
        });
    });

    return result;
  }, [templateVersions, templates]);

  useEffect(() => {
    const peopleNeeded = () => {
      let result: Person[] = [];

      const currentUser = user.getUserInfo();
      if (currentUser) {
        let sqlUser = SQLUsers.find(p => p.email === currentUser.email);
        if (sqlUser) setSqlUserId(sqlUser?._id);
      }

      if (props.records) {
        let peopleIds: string[] = [];
        let usersIds: string[] = [];
        let usersEmailss: string[] = [];

        props.records.forEach(function (r, i) {
          try {
            if (props.hasMetaData === true && r.metadataJSON) {
              let meta = JSON.parse(r.metadataJSON);

              // Search the metadata for all keys that may contain people needed

              if (meta[METADATA_KEYS.WORKERID]) {
                let mongoSqlId: MongoIdSqlId = JSON.parse(
                  meta[METADATA_KEYS.WORKERID].toString(),
                );
                if (mongoSqlId && mongoSqlId.mongoId)
                  peopleIds.push(mongoSqlId.mongoId);
              }

              if (meta[METADATA_KEYS.PERSON_INVOLVED]) {
                let mongoSqlId: MongoIdSqlId = JSON.parse(
                  meta[METADATA_KEYS.PERSON_INVOLVED].toString(),
                );
                if (mongoSqlId && mongoSqlId.mongoId)
                  peopleIds.push(mongoSqlId.mongoId);
              }

              if (meta[METADATA_KEYS.ASSIGNED_TO]) {
                let mongoSqlId: MongoIdSqlId = JSON.parse(
                  meta[METADATA_KEYS.ASSIGNED_TO].toString(),
                );

                if (mongoSqlId && mongoSqlId.mongoId)
                  usersIds.push(mongoSqlId.mongoId);
              }
            }
          } catch (err) {
            //Invalid meta data
          }

          usersEmailss.push(r.updatedBy ?? r.createdBy);
        });

        let usersResult = SQLUsers.filter(
          x => usersIds.includes(x._id) || usersEmailss.includes(x.email ?? ''),
        );
        let personUsersIds = usersResult.map(x => x.SQLServerPersonId);
        result = people.filter(
          x =>
            peopleIds.includes(x._id) || personUsersIds.includes(x.SQLServerId),
        );

        setPeopleNeeded(result);
        setUsersNeeded(usersResult);
      }
    };

    if (people.length > 0) peopleNeeded();
  }, [props.records, people]);

  useEffect(() => {
    const getRelationsNeeded = async () => {
      let result: SubmissionRelation[] = [];

      if (props.records) {
        let submissionIds = props.records.map(r => r._id);
        result = submissionRelations.filter(x =>
          submissionIds.includes(x.submissionId),
        );
      }

      setRelationsNeeded(result);
    };

    getRelationsNeeded();
  }, [props.records]);

  const equipmentNeeded = useMemo<
    {id: string; name: string; type: string}[]
  >(() => {
    let result: {id: string; name: string; type: string}[] = [];
    if (props.records) {
      let equipmentIds: string[] = [];

      props.records.forEach(function (r, i) {
        if (props.hasMetaData === true && r.metadataJSON) {
          let meta = JSON.parse(r.metadataJSON);

          if (
            props.metaDataKey_title &&
            props.metaDataKey_title == METADATA_KEYS.EQUIPMENTID &&
            meta[props.metaDataKey_title]
          )
            equipmentIds.push(meta[props.metaDataKey_title!].toString());
        }
      });

      result = equipmentVehicleProfiles
        .filter(x => equipmentIds.includes(x._id))
        .map(e => ({
          id: e._id,
          name: e.name + ' ' + e.number,
          type: ListDataTypes.EQUIPMENTVEHICLEPROFILES,
        }));

      result = result.concat(
        equipmentHeavyProfiles
          .filter(x => equipmentIds.includes(x._id))
          .map(e => ({
            id: e._id,
            name: e.name + ' ' + e.number,
            type: ListDataTypes.EQUIPMENTHEAVYPROFILES,
          })),
      );

      result = result.concat(
        equipmentOtherProfiles
          .filter(x => equipmentIds.includes(x._id))
          .map(e => ({
            id: e._id,
            name: e.name + ' ' + e.number,
            type: ListDataTypes.EQUIPMENTOTHERPROFILES,
          })),
      );
    }

    return result;
  }, [props.records]);

  useEffect(() => {
    if (props.setUnselectCommand)
      props.setUnselectCommand(() => {
        setSelectedRecord(undefined);
      });

    let elevateReport = false;
    let deleteReport = false;
    let investigateReport = false;
    if (props.category == CATEGORY.REPORTS) {
      elevateReport = hasPermission(PERMISSIONS.REPORT_ELEVATE);
      deleteReport = hasPermission(PERMISSIONS.REPORT_DELETE);
      investigateReport = hasPermission(PERMISSIONS.REPORT_INVESTIGATION);
    } else if (props.category == CATEGORY.EQUIPMENT) {
      deleteReport = hasPermission(PERMISSIONS.REPORT_EQUIPMENT_DELETE);
    } else if (props.category == CATEGORY.OPERATIONS) {
      deleteReport = hasPermission(PERMISSIONS.REPORT_OPERATION_DELETE);
    } else if (props.category == CATEGORY.PEOPLE) {
      deleteReport = hasPermission(PERMISSIONS.REPORT_PEOPLE_DELETE);
    }

    setCanElevate(elevateReport);
    setHasDeletePermission(deleteReport);
    setHasInvestigationPermission(investigateReport);
  }, [powersyncReady]);

  useEffect(() => {
    const getInvestigationTemplate = async () => {
      let filteredTemplates = templates
        .filter(
          x =>
            x.templateCategory.templateCategoryName ==
            TemplateCategoryTypes.REPORTS,
        )
        .map(x => x._id);

      setTemplatesWithInvestigation(filteredTemplates);
    };

    getInvestigationTemplate();
  }, [powersyncReady]);

  useEffect(() => {
    if (!isFocused) {
      setSelectedRecord(undefined);
      setCanConvertSelected(false);
    }
  }, [isFocused]);

  useEffect(() => {
    if (
      props.records &&
      allVersionsNeeded.length > 0 &&
      templatesNeeded.length > 0
    ) {
      const mappedRecords: Record[] = [];
      props.records.forEach(function (r, i) {
        let version = allVersionsNeeded.find(
          v => r.templateVersion._id === v.id,
        );

        let title = version?.name!.trim() ?? '';
        let date = moment(
          r.updateDateTimeStamp ?? r.createDateTimeStamp,
        ).toDate();

        let personId = '';
        let firstAidAttendant = '';
        let equipmentType = '';
        let equipmentId = '';
        let assignedText = '';
        let investigationRequired = false;
        let hasInvestigation = false;
        let investigationClosed = false;
        if (props.hasMetaData === true) {
          if (r.metadataJSON) {
            let meta = JSON.parse(r.metadataJSON);

            let personMeta = '';
            if (meta[METADATA_KEYS.PERSON_INVOLVED])
              personMeta = meta[METADATA_KEYS.PERSON_INVOLVED].toString();
            else if (meta[METADATA_KEYS.WORKERID])
              personMeta = meta[METADATA_KEYS.WORKERID].toString();

            if (personMeta) {
              let mongoSqlId: MongoIdSqlId = JSON.parse(personMeta);
              personId = mongoSqlId.mongoId ?? '';
            }

            if (meta[METADATA_KEYS.FIRST_AID_ATTENDANT]) {
              let mongoSqlId: MongoIdSqlId = JSON.parse(
                meta[METADATA_KEYS.FIRST_AID_ATTENDANT].toString(),
              );
              firstAidAttendant = mongoSqlId.mongoId ?? '';
            }

            if (props.metaDataKey_title && meta[props.metaDataKey_title]) {
              if (props.metaDataKey_title == METADATA_KEYS.EQUIPMENTID) {
                let equipment = equipmentNeeded.find(
                  x => x.id === meta[props.metaDataKey_title!].toString(),
                );

                if (equipment) {
                  equipmentType = equipment.type;
                  equipmentId = equipment.id;
                  title += ': ' + equipment.name;
                }
              } else if (props.metaDataKey_title == METADATA_KEYS.WORKERID) {
                let mongoSqlId: MongoIdSqlId = JSON.parse(
                  meta[props.metaDataKey_title!].toString(),
                );

                let worker = peopleNeeded.find(
                  x => x._id == mongoSqlId.mongoId,
                );

                if (worker)
                  title += ': ' + worker.firstName + ' ' + worker.lastName;

                personId = mongoSqlId.mongoId ?? '';
              } else title = meta[props.metaDataKey_title];
            }

            if (props.metaDataKey_project && meta[props.metaDataKey_project])
              title += ' at ' + meta[props.metaDataKey_project];

            let metaDataKeyDate = '';
            let metaDataKeyTime = '';
            if (r.templateType.name === TemplateTypes.TASK) {
              metaDataKeyDate = METADATA_KEYS.DUEDATE;
              metaDataKeyTime = METADATA_KEYS.DUETIME;
            } else if (r.templateType.name === TemplateTypes.INCIDENT) {
              metaDataKeyDate = METADATA_KEYS.INCIDENTDATE;
              metaDataKeyTime = METADATA_KEYS.INCIDENTTIME;
            }

            let datePart = '';
            if (metaDataKeyDate) datePart = meta[metaDataKeyDate];

            let timePart = '';
            if (metaDataKeyTime) timePart = meta[metaDataKeyTime];

            if (datePart && timePart) {
              let time = moment(timePart, Formats.BACKEND_TIME);

              date = moment(datePart, Formats.BACKEND_DATE)
                .add(time.hours(), 'hours')
                .add(time.minutes(), 'minutes')
                .toDate();
            }

            if (props.showInvestigation) {
              if (meta[METADATA_KEYS.INVESTIGATION_REQUIRED])
                investigationRequired =
                  meta[METADATA_KEYS.INVESTIGATION_REQUIRED] == 'Yes';

              if (meta[METADATA_KEYS.HAS_INVESTIGATION])
                hasInvestigation =
                  meta[METADATA_KEYS.HAS_INVESTIGATION] == 'Yes';

              if (meta[METADATA_KEYS.INVESTIGATION_CLOSED])
                investigationClosed =
                  meta[METADATA_KEYS.INVESTIGATION_CLOSED] == 'Yes';
            }

            if (props.showAssignedTo) {
              if (meta[METADATA_KEYS.ASSIGNED_TO]) {
                try {
                  let mongoSqlId: MongoIdSqlId = JSON.parse(
                    meta[METADATA_KEYS.ASSIGNED_TO],
                  );
                  // Is Assigned To Me
                  if (mongoSqlId.mongoId == sqlUserId) {
                    let assignedBy = usersNeeded.find(
                      x => x.email == r.createdBy,
                    );

                    if (assignedBy && assignedBy.firstName)
                      assignedText =
                        'Assigned By: ' +
                        assignedBy.firstName +
                        ' ' +
                        (assignedBy.lastName ?? '');
                  } else if (mongoSqlId.label)
                    assignedText = 'Assigned To: ' + mongoSqlId.label;
                } catch (error) {
                  console.log('Error: Bad metadata serialization');
                }
              }
            }
          }
        }

        let template = templatesNeeded.find(
          t => r?.templateVersion._id === t.versionId,
        );

        let project = relationsNeeded.find(
          x => r._id === x.submissionId && x.relationType === 'Project',
        );

        let isLocked;

        if (r.submissionStatusLock) {
          isLocked =
            r.submissionStatusLock == LOCK_STATUS.LOCK ||
            r.submissionStatusLock == LOCK_STATUS.SUPERLOCK
              ? true
              : false;
        } else {
          isLocked = false;
        }

        if (r.displayId) {
          title = r.displayId + ' - ' + title;
        }

        mappedRecords.push({
          key: i,
          id: r._id,
          templateId: template?.templateId,
          personId: personId,
          firstAidAttendant: firstAidAttendant,
          projectId: project && project.relationId ? project.relationId : '',
          equipmentType: equipmentType,
          equipment: equipmentId,
          name: title,
          status: r.submissionStatus.name!,
          isLocked: isLocked,
          datetime: date,
          icon: version?.icon ?? Icons.TASK,
          assignedText: assignedText,
          createdBy: r.createdBy,
          investigationRequired: investigationRequired,
          hasInvestigation: hasInvestigation,
          investigationClosed: investigationClosed,
          displayId: r.displayId ?? null,
        });
      });

      setRecords(prev => {
        const prevSerialized = JSON.stringify(prev);
        const nextSerialized = JSON.stringify(mappedRecords);
        return prevSerialized !== nextSerialized ? mappedRecords : prev;
      });
    }
  }, [
    props.records,
    templateVersions,
    templates,
    peopleNeeded,
    equipmentNeeded,
    props.category,
  ]);

  useEffect(() => {
    if (props.formattedRecords) setRecords(props.formattedRecords);
  }, [props.formattedRecords]);

  useEffect(() => {
    let checkIsConvertible = async () => {
      let canConvert = await canConvertSubmission(selectedRecord?.id!);
      if (canConvert) setCanConvertSelected(true);
      else setCanConvertSelected(false);
    };

    if (selectedRecord) checkIsConvertible();

    let checkCanDelete = async () => {
      //If user can delete all reports set canDelete to true if it isn't already true
      //If they can't delete all reports check if they created the report and set it to true, false otherwise
      if (hasDeletePermission) {
        if (canDelete) return;
        else {
          setCanDelete(true);
          return;
        }
      }

      let userCreatedRecord = false;
      let currentUser = user.getUserInfo();

      if (
        selectedRecord &&
        currentUser &&
        selectedRecord.createdBy === currentUser.email
      )
        userCreatedRecord = true;

      setCanDelete(userCreatedRecord);
    };

    checkCanDelete();
  }, [selectedRecord]);

  const recordPressed = (record: Record) => {
    if (props.onRecordPressed) {
      props.onRecordPressed(record.id);
    } else {
      props.navigation.navigate(Navigation.DYNAMICFORM, {
        submissionId: record.id,
      });
    }
  };

  const showPDFModal = (record: Record) => {
    setRecordToPDF(record);
    setIsShowingPDFModal(true);
  };

  const convertRecord = async (submissionId: string) => {
    if (!canElevate) {
      elevateModalCommand.current.openModal();
      return;
    }

    let childId = submissionId;
    let parentId = await convertSubmission(childId);
    if (parentId) {
      let orgid = user.getUserInfo().orgId.toString();
      let newSubmissionLink: SubmissionLink = {
        _id: new ObjectId().toString(),
        parentSubmissionId: parentId,
        childSubmissionId: childId,
        partition: orgid.toString(),
      };
      await createSubmissionLink(newSubmissionLink);
      props.navigation.push(Navigation.DYNAMICFORM, {
        submissionId: parentId,
        showConfirmation: false,
      });
    }
  };

  const deleteRecord = async () => {
    if (selectedRecord) {
      // Remove submission links by parent
      if (
        submissionLinks.some(s => s.parentSubmissionId === selectedRecord.id)
      ) {
        let deleteIds = submissionLinks
          .filter(x => x.parentSubmissionId == selectedRecord.id)
          .map(x => x._id);
        deleteItems(deleteIds, 'SubmissionLinks');
      }

      // Remove submission links by child
      if (
        submissionLinks.some(s => s.childSubmissionId === selectedRecord.id)
      ) {
        let deleteIds = submissionLinks
          .filter(x => x.childSubmissionId == selectedRecord.id)
          .map(x => x._id);
        deleteItems(deleteIds, 'SubmissionLinks');
      }

      await deleteSubmissionAttachments(selectedRecord.id!);
      await deleteItems([selectedRecord.id!], 'Submissions');

      let newRecords = [...records.filter(x => x.id != selectedRecord.id!)];
      setRecords(newRecords);
    }
  };

  const renderRecordGroup = (
    records: Record[],
    title: string,
  ): React.ReactElement => {
    return (
      <>
        {records.length > 0 && (
          <>
            <Text style={ReportsStyleSheet.listSubtitle}>{title}</Text>
            {records.map((record, index) => {
              return renderRecord(false, record, index);
            })}
          </>
        )}
      </>
    );
  };

  const renderSubmitted = (): React.ReactElement => {
    if (submittedTotal === 0) return <></>;

    return (
      <View style={ReportsStyleSheet.list}>
        <Text style={ReportsStyleSheet.listTitle}>
          {props.title ?? 'Submitted'}
        </Text>

        {submittedRecords.map((record, index) =>
          renderRecord(false, record, index),
        )}

        {submittedPaginator}
      </View>
    );
  };

  const renderDrafts = (): React.ReactElement => {
    if (draftTotal === 0) return <></>;

    return (
      <View style={ReportsStyleSheet.list}>
        <Text style={ReportsStyleSheet.listTitle}>Draft</Text>

        {draftRecords.map((record, index) => renderRecord(true, record, index))}

        {draftPaginator}
      </View>
    );
  };

  const renderConvertAction = (index: number): React.ReactElement => {
    let submissionId = selectedRecord?.id;

    if (!canConvertSelected) return <></>;

    return (
      <CustomPressable
        key='convertaction'
        onPress={() => convertRecord(submissionId!)}
        style={({pressed}) => [
          !canElevate && {
            opacity: 0.4,
          },
          pressed && {
            opacity: 0.6,
          },
          ReportsStyleSheet.draftAction,
          {paddingLeft: 5, paddingRight: 9},
        ]}
        tooltip='Elevate to Full'>
        <Icon icon={'convert'} color={Colors.white} size={20} />
      </CustomPressable>
    );
  };

  const confirmDeleteRecord = () => {
    if (canDelete) deleteModalCommand.current.openModal();
    else noDeleteModalCommand.current.openModal();
  };

  const openInvestigation = async () => {
    if (selectedRecord?.id) {
      let subLinks = submissionLinks
        .filter(x => x.parentSubmissionId === selectedRecord?.id)
        .map(x => x.childSubmissionId);

      let investigation = submissions.find(
        x =>
          subLinks.includes(x._id) &&
          x.templateType.name == TemplateTypes.INVESTIGATION,
      );

      if (investigation) {
        props.navigation.push(Navigation.DYNAMICFORM, {
          submissionId: investigation._id,
        });
      } else await createInvestigation();
    }
  };

  const createInvestigation = async () => {
    let submissionId = selectedRecord?.id;
    let investigationId = new ObjectId().toString();

    let newTemplate = templates.find(
      x =>
        x.templateCategory.templateCategoryName ==
        TemplateCategoryTypes.INVESTIGATION,
    );

    if (!newTemplate) {
      console.error('No Template found!');
      return;
    }

    let filteredVersions = templateVersions
      .filter(x => x.templateId === newTemplate!._id)
      .sort((a, b) => b.version - a.version)
      .map(x => ({_id: x._id, name: x.name}));

    if (filteredVersions.length == 0) {
      console.error('No Version found!');
      return;
    }

    const draftStatus: SubmissionStatus = db_submissionStatuses.find(
      x => x.name == 'Draft',
    )!;

    let currentUser = user.getUserInfo();
    let org = '';
    let currentEmail = '';

    if (currentUser) {
      org = currentUser.orgId.toString();
      currentEmail = currentUser.email;
    }

    let newVersion = filteredVersions[0];

    let newInvestigation: Submission = {
      _id: investigationId,
      partition: org,
      createdBy: currentEmail as string,
      updatedBy: currentEmail as string,
      templateVersion: {
        _id: newVersion!._id,
        name: newVersion!.name,
      },
      templateType: {
        _id: newTemplate!.templateType._id,
        name: newTemplate!.templateType.name,
      },
      submissionStatus: {
        _id: draftStatus._id,
        name: draftStatus.name,
      },
      SQLServerId: new UUID().toString().toUpperCase(),
      answers: [],
      metadataJSON: '',
      createDateTimeStamp: new Date(),
      updateDateTimeStamp: new Date(),
      attachmentsUploaded: true,
    };

    await createSubmission(newInvestigation);

    let submission = submissions.find(x => x._id === submissionId!);
    if (submission) {
      let meta = null;

      if (submission.metadataJSON) meta = JSON.parse(submission.metadataJSON);

      if (meta === null) meta = {};

      meta[METADATA_KEYS.HAS_INVESTIGATION] = 'Yes';

      submission.metadataJSON = JSON.stringify(meta);

      await updateSubmission(submission);
    }

    if (investigationId == null)
      throw Error('Error: create investigation failed!');

    let newSubmissionLink: SubmissionLink = {
      _id: new ObjectId().toString(),
      parentSubmissionId: submissionId!,
      childSubmissionId: investigationId,
      partition: org,
    };

    createSubmissionLink(newSubmissionLink);

    props.navigation.navigate(Navigation.DYNAMICFORM, {
      submissionId: investigationId,
      submissionStatus: 'Draft',
    });
  };

  const renderActions = (
    canConvert: boolean,
    canDeleteDraft: boolean,
    canInvestigate: boolean,
    index: number,
  ): React.ReactElement => {
    const actions = (
      <View
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
        }}>
        {canConvert && renderConvertAction(index)}
        {canDeleteDraft && (
          <CustomPressable
            onPress={() => {
              confirmDeleteRecord();
            }}
            style={({pressed}) => [
              pressed && {
                opacity: 0.6,
              },
              !canDelete && {
                opacity: 0.4,
              },
              ReportsStyleSheet.draftAction,
            ]}
            tooltip='Delete'>
            <Icon icon={'trash'} color={Colors.white} size={20} />
          </CustomPressable>
        )}
        {canInvestigate && (
          <CustomPressable
            onPress={() => {
              openInvestigation();
            }}
            style={({pressed}) => [
              pressed && {
                opacity: 0.6,
              },
              ReportsStyleSheet.draftAction,
              {padding: 5},
            ]}
            tooltip='Investigation'>
            <Icon icon={'investigation'} color={Colors.white} size={26} />
          </CustomPressable>
        )}
        <CustomPressable
          onPress={() => showPDFModal(selectedRecord!)}
          style={({pressed}) => [
            pressed && {
              opacity: 0.6,
            },
            ReportsStyleSheet.draftAction,
          ]}
          tooltip={'Generate PDF'}>
          <Icon icon={'print'} color={Colors.white} size={20} />
        </CustomPressable>
        <CustomPressable
          onPress={() => {
            recordPressed(selectedRecord!);
          }}
          style={({pressed}) => [
            pressed && {
              opacity: 0.6,
            },
            ReportsStyleSheet.draftAction,
          ]}
          tooltip={'View' + (canDelete ? ' / Edit' : '')}>
          <Icon icon={'eye'} color={Colors.white} size={20} />
        </CustomPressable>
      </View>
    );

    return actions;
  };

  const renderDate = (record: Record): React.ReactElement => {
    let sub = props.records?.find(x => String(x._id) == record.id);
    if (!sub) return <></>;
    let date = moment(record.datetime).format('MMM DD');
    let time = moment(record.datetime).format('HH:mm');

    let backgroundColor = Colors.white;
    let color = Colors.darkGreen;

    if (sub.templateType.name === TemplateTypes.TASK) {
      let status = sub?.submissionStatus.name;
      if (status === SubmissionStatuses.COMPLETE) {
        backgroundColor = Colors.teal;
        color = Colors.white;
      } else if (status === SubmissionStatuses.DISMISSED) {
        backgroundColor = Colors.gray;
        color = Colors.white;
      } else if (record.datetime < moment().toDate()) {
        backgroundColor = Colors.red;
        color = Colors.white;
      }
    }

    return (
      <View
        style={[
          ReportsStyleSheet.listItemDateContent,
          {backgroundColor: backgroundColor},
        ]}>
        <Text style={[ReportsStyleSheet.listItemDate, {color: color}]}>
          {date}
        </Text>
        <Text style={[ReportsStyleSheet.listItemDate, {color: color}]}>
          {time}
        </Text>
      </View>
    );
  };

  const renderRecord = (
    isDraft: boolean,
    record: Record,
    index: number,
  ): React.ReactElement => {
    let canConvert = false,
      canDeleteDraft = false;

    if (isDraft) canDeleteDraft = true;
    else canConvert = true;

    let canInvestigate = false;
    let investigationIcon = null;
    let investigationTooltip = '';
    if (props.showInvestigation) {
      if (
        hasInvestigationPermission &&
        record.templateId &&
        templatesWithInvestigation.includes(record.templateId) &&
        !isDraft
      )
        canInvestigate = true;

      if (record.investigationRequired && !record.hasInvestigation) {
        investigationIcon = Icons.INVESTIGATION;
        investigationTooltip = 'Missing investigation';
      } else if (record.hasInvestigation && !record.investigationClosed) {
        investigationIcon = Icons.INVESTIGATION.replaceAll(
          Colors.red,
          Colors.yellow,
        );
        investigationTooltip = 'Open investigation';
      } else if (record.hasInvestigation && record.investigationClosed) {
        investigationIcon = Icons.INVESTIGATION.replaceAll(
          Colors.red,
          Colors.darkestGreen,
        );
        investigationTooltip = 'Investigation Closed';
      }
    }

    return (
      <View
        key={record.key}
        style={{
          flexDirection: 'row',
          borderBottomWidth: 0.4,
          borderBottomColor: Colors.darkGreen,
        }}>
        <Pressable
          style={({pressed}) => [
            ReportsStyleSheet.listItemContainer,
            {flex: 1},
            props.showAssignedTo && {paddingVertical: 18},
            pressed && {
              backgroundColor: Colors.darkGreenTransparent,
            },
          ]}
          onPress={() => setSelectedRecord(record)}
          disabled={props.disabled}>
          <View style={{marginHorizontal: 12}}>
            {props.useLocalIcons ? (
              <Icon
                icon={record.icon as IconValue}
                color={Colors.darkestGreen}
                size={24}
              />
            ) : (
              <>
                <img
                  src={`data:image/svg+xml;utf8,${encodeURIComponent(
                    record.icon,
                  )}`}
                  style={{color: Colors.darkGreen}}
                />
              </>
            )}
          </View>
          <Text style={ReportsStyleSheet.listItemName} numberOfLines={2}>
            {record.name}
          </Text>
          {props.showAssignedTo ? (
            <Text
              style={[
                ReportsStyleSheet.listItemDate,
                {
                  position: 'absolute',
                  left: 46,
                  bottom: 4,
                  fontSize: 12,
                  paddingHorizontal: 6,
                  backgroundColor: Colors.lightTeal,
                  borderRadius: 6,
                },
              ]}
              numberOfLines={1}>
              {record.assignedText}
            </Text>
          ) : null}
          <View style={{flexDirection: 'row', gap: 5, marginRight: 5}}>
            {investigationIcon && (
              <CustomPressable tooltip={investigationTooltip} tooltipSize='lg'>
                <img
                  src={`data:image/svg+xml;utf8,${encodeURIComponent(
                    investigationIcon,
                  )}`}
                  style={{width: 22, marginTop: -2}}
                />
              </CustomPressable>
            )}
            {record.isLocked && (
              <Icon icon={'lock'} color={Colors.darkestGreen} size={16} />
            )}
          </View>
          {renderDate(record)}
        </Pressable>
        {selectedRecord?.id == record.id &&
          renderActions(canConvert, canDeleteDraft, canInvestigate, index)}
      </View>
    );
  };

  const renderPDFModal = (): React.ReactElement => {
    return (
      <Modal
        visible={isShowingPDFModal}
        transparent={true}
        statusBarTranslucent={true}
        animationType='fade'>
        <View style={ControlsStyleSheet.modalBackground}>
          <View style={ControlsStyleSheet.groupSelectorModal}>
            <View style={ControlsStyleSheet.groupSelectorModalBar}>
              <Text style={ControlsStyleSheet.groupSelectorModalTitle}>
                Download Documents
              </Text>
              <Pressable
                style={({pressed}) => [
                  ControlsStyleSheet.groupSelectorModalClose,
                  pressed && {
                    backgroundColor: Colors.darkGreenTransparent,
                    borderRadius: 24,
                  },
                ]}
                onPress={() => setIsShowingPDFModal(false)}>
                <Icon icon={'close'} color={Colors.darkestGreen} size={24} />
              </Pressable>
            </View>
            <ScrollView
              keyboardShouldPersistTaps='handled'
              style={{marginVertical: 24}}>
              <View style={ControlsStyleSheet.attachmentsContainer}>
                {isShowingPDFModal && recordToPDF && (
                  <DynamicPDF
                    submissionId={recordToPDF.id}
                    type='submission'></DynamicPDF>
                )}
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>
    );
  };

  return (
    <View>
      {props.showSearch !== false && (
        <SearchBox
          onChangeSearch={newSearch => setSearch(newSearch)}
          onChangeFilter={filterValue => {
            if (props.onChangeFilter) props.onChangeFilter(filterValue);
          }}
          showFilters={props.showFilters}
          filters={props.filters}
          filterValue={props.filterValue}
          category={props.category}
          disabled={props.disabled}
          showSorters={props.showSorters}
          sortConfig={props.sortConfig}
          setSortConfig={props.setSortConfig}
        />
      )}
      <ConfirmModal
        title='No Access'
        message='You do not have access to elevate this report'
        okClick={() => {}}
        okText='Ok'
        setOpenModal={(func: () => void) => {
          elevateModalCommand.current.setOpenModal(func);
        }}
      />
      <ConfirmModal
        title='No Access'
        message='You do not have access to delete this report'
        okClick={() => {}}
        okText='Ok'
        setOpenModal={(func: () => void) => {
          noDeleteModalCommand.current.setOpenModal(func);
        }}
      />
      <ConfirmModal
        title='Delete this record'
        message='Are you sure you want to delete this record?'
        cancelClick={() => {}}
        cancelText='No'
        okClick={() => {
          deleteRecord();
        }}
        okText='Delete'
        setOpenModal={(func: () => void) => {
          deleteModalCommand.current.setOpenModal(func);
        }}
      />
      {!draftTotal && !submittedTotal ? (
        <View style={ReportsStyleSheet.list}>
          <Text
            style={[
              ProjectSitesStyleSheet.siteItemText,
              {textAlign: 'center'},
            ]}>
            No Results Found
          </Text>
        </View>
      ) : null}
      {props.showDrafts && renderDrafts()}
      {renderSubmitted()}
      {renderPDFModal()}
    </View>
  );
};

export default RecordList;
